import { motion, useInView } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "../version/reducedMotion";

// Einblenden beim Scrollen nach Styleguide Kap. 11: 24 px von unten, 600 ms, --ease cubic-bezier(.2,.7,.2,1).
// Bei „Bewegung reduzieren“ sofort sichtbar. Einmal sichtbar → bleibt bei Opacity 1 (A6).
export const Reveal = ({ children, delay = 0, y = 24, className = "", as = "div", ...rest }) => {
    const reduce = useReducedMotion();
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, amount: 0.01, margin: "40px 0px 40px 0px" });
    const [shown, setShown] = useState(reduce);
    useEffect(() => { if (inView || reduce) setShown(true); }, [inView, reduce]);
    // Sicherheitsnetz: wenn IntersectionObserver (z. B. mit Lenis) nicht greift, nach kurzem Sichtkontakt erzwingen
    useEffect(() => {
        if (reduce || shown || !ref.current) return undefined;
        const el = ref.current;
        const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) setShown(true); }, { rootMargin: "80px", threshold: 0.01 });
        io.observe(el);
        return () => io.disconnect();
    }, [reduce, shown]);
    const M = motion[as] || motion.div;
    return (
        <M
            ref={ref}
            className={className}
            initial={reduce ? false : { opacity: 0, y }}
            animate={shown ? { opacity: 1, y: 0 } : { opacity: 0, y }}
            transition={{ duration: 0.6, delay: shown ? delay : 0, ease: [0.2, 0.7, 0.2, 1] }}
            {...rest}
        >
            {children}
        </M>
    );
};

export const KineticLines = ({ lines, className = "", delay = 0, lineClassName = "", lastLineClassName = "" }) => (
    <span className={className}>
        {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
                <motion.span
                    className={`block will-change-transform ${lineClassName} ${i === lines.length - 1 ? lastLineClassName : ""}`}
                    initial={{ y: "115%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.8, delay: delay + i * 0.12, ease: [0.2, 0.7, 0.2, 1] }}
                >
                    {line}
                </motion.span>
            </span>
        ))}
    </span>
);

export const SectionHead = ({ label, title, sub, className = "" }) => (
    <div className={className}>
        {label && (
            <Reveal>
                <span className="font-mono-tech text-xs tracking-[0.25em] text-ember uppercase" data-testid="section-label">
                    {label}
                </span>
            </Reveal>
        )}
        <Reveal delay={0.08}>
            <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-ink mt-5 max-w-3xl" data-testid="section-title">
                {title}
            </h2>
        </Reveal>
        {sub && (
            <Reveal delay={0.16}>
                <p className="text-petrol-300 text-base md:text-lg mt-6 max-w-2xl leading-relaxed" data-testid="section-sub">
                    {sub}
                </p>
            </Reveal>
        )}
    </div>
);
